import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/lib/supabase";
import { format } from "date-fns";
import { PaymentPlanView } from "./PaymentPlanView";
import { PaymentPlanEdit } from "./PaymentPlanEdit";
import { generatePaymentSchedule } from "./PaymentCalculator";
import { PaymentSchedule } from "./PaymentSchedule";
import { Card } from "@/components/ui/card";

interface ClientPaymentPlanProps {
  clientId: number;
  existingPlan?: any;
  mode?: 'admin' | 'client';
}

export const ClientPaymentPlan = ({ clientId, existingPlan, mode = 'admin' }: ClientPaymentPlanProps) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(!existingPlan);
  const [currentPlan, setCurrentPlan] = useState(existingPlan);
  const [paymentPlan, setPaymentPlan] = useState({
    amount_agreed: existingPlan?.amount_agreed || "",
    first_payment_date: existingPlan?.first_payment_date 
      ? format(new Date(existingPlan.first_payment_date), 'yyyy-MM-dd')
      : "",
    amount_paid: existingPlan?.amount_paid || "0",
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const dataToSubmit = {
        client_id: clientId,
        amount_agreed: Number(paymentPlan.amount_agreed),
        first_payment_date: paymentPlan.first_payment_date,
        amount_paid: Number(paymentPlan.amount_paid),
      };

      let result;
      
      if (currentPlan) {
        result = await supabase
          .from('payment_plans')
          .update(dataToSubmit)
          .eq('id', currentPlan.id)
          .select()
          .single();
      } else {
        result = await supabase
          .from('payment_plans')
          .insert([dataToSubmit])
          .select()
          .single();
      }

      if (result.error) throw result.error;

      setCurrentPlan(result.data);
      setPaymentPlan({
        amount_agreed: result.data.amount_agreed?.toString() || "",
        first_payment_date: result.data.first_payment_date 
          ? format(new Date(result.data.first_payment_date), 'yyyy-MM-dd')
          : "",
        amount_paid: result.data.amount_paid?.toString() || "0",
      });
      setIsEditing(false);

      toast({
        title: currentPlan ? "Payment Plan Updated" : "Payment Plan Created",
        description: "The payment plan has been successfully saved.",
      });
    } catch (error: any) {
      console.error('Error saving payment plan:', error);
      toast({
        variant: "destructive",
        title: "Error Saving Payment Plan",
        description: error.message || "Failed to save payment plan. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  if (!isEditing && currentPlan) {
    const paymentSchedule = generatePaymentSchedule({
      amountAgreed: Number(currentPlan.amount_agreed),
      firstPaymentDate: currentPlan.first_payment_date,
      createdAt: currentPlan.created_at
    });

    return (
      <Card className="p-6 bg-[#222222] border-white/10">
        <h3 className="text-lg font-semibold text-white mb-4">Payment Plan & Overview</h3>
        <div className="space-y-6">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-gray-400">Monthly Payment Amount:</p>
              <p className="text-white font-medium text-lg">R{Number(currentPlan.amount_agreed).toFixed(2)}</p>
            </div>
            
            <div>
              <p className="text-gray-400">Amount Paid So Far:</p>
              <p className="text-green-400 font-medium text-lg">R{Number(currentPlan.amount_paid).toFixed(2)}</p>
            </div>
            
            <div>
              <p className="text-gray-400">Outstanding Balance:</p>
              <p className="text-red-400 font-medium text-lg">
                R{(Number(currentPlan.amount_agreed) - Number(currentPlan.amount_paid)).toFixed(2)}
              </p>
            </div>
            
            <div>
              <p className="text-gray-400">First Payment Date:</p>
              <p className="text-white font-medium text-lg">
                {format(new Date(currentPlan.first_payment_date), 'dd MMM yyyy')}
              </p>
            </div>
          </div>

          <div className="mt-4 p-4 bg-gray-900/50 rounded-lg">
            <h5 className="text-white font-medium mb-2">Payment Structure</h5>
            <ul className="space-y-2 text-sm">
              <li className="text-gray-300">• First 3 months: R{Number(currentPlan.amount_agreed).toFixed(2)} per month to company</li>
              <li className="text-gray-300">
                • From month 4: R{(Number(currentPlan.amount_agreed) * 0.1).toFixed(2)} to company, 
                R{(Number(currentPlan.amount_agreed) * 0.9).toFixed(2)} to creditors per month
              </li>
            </ul>
          </div>

          {mode === 'admin' && (
            <button
              onClick={() => setIsEditing(true)}
              className="w-full px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
            >
              Edit Payment Plan
            </button>
          )}

          <PaymentSchedule
            payments={paymentSchedule}
            amountPaid={Number(currentPlan.amount_paid)}
          />
        </div>
      </Card>
    );
  }

  return (
    <PaymentPlanEdit
      paymentPlan={paymentPlan}
      onChange={(field, value) => setPaymentPlan(prev => ({ ...prev, [field]: value }))}
      onSubmit={handleSubmit}
      loading={loading}
    />
  );
};